import { CommonModule } from '@angular/common';
import {
  type AfterViewInit,
  Component,
  computed,
  inject,
  input,
  output,
  type OutputEmitterRef,
  type Signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { type ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import {
  MatAutocompleteModule,
  type MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { type MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { throttle } from 'lodash-es';
import { BehaviorSubject, EMPTY, type Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';

import type { ICountry, ILeiProfile } from '@core/models/app.model';
import type { IPaginatedResponse } from '@core/models/pagination-params.model';
import { ErrorMessageService } from '@core/services/error-message.service';
import { NotifyService } from '@core/services/notifyService.service';
import { getCompanyNameFromLei } from '@helpers/get-compnay-name-from-lei';
import { DIALOG_SIZE } from '@shared/constants/ume-constants';
import { LeiSearchDialogComponent } from '@shared/forms/lei-search-dialog/lei-search-dialog.component';
import type { CountryInputValue } from '@shared/forms/mat-country-input/mat-country-input.component';
import { LEIDatabaseService } from '@shared/services/lei-database.service';
import { UmeFlagComponent } from '@widgets/ume-flag/ume-flag.component';

interface ICompanyNamesAutocomplete {
  lei: ILeiProfile;
  names: string[];
}

@UntilDestroy()
@Component({
  selector: 'ume-entity-name-input',
  templateUrl: './entity-name-input.component.html',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    UmeFlagComponent,
  ],
})
export class EntityNameInputComponent implements ControlValueAccessor, AfterViewInit {
  private readonly _matDialog = inject(MatDialog);
  private readonly _leiDatabaseService = inject(LEIDatabaseService);
  private readonly _notifyService = inject(NotifyService);
  public errorsService = inject(ErrorMessageService);

  country = input<CountryInputValue>();
  disabled = input(false);
  fieldAppearance = input<MatFormFieldAppearance>('fill');
  originallyRequestedName = input<string | null>(null);
  isShowLEISearch = input(false);
  isLeiRequestBlocked = input(false);
  noLabel = input(false);
  fitShape = input(false);
  noSubWrapper = input(false);
  searchOnlyByLEI = input<string[]>([]);

  nameChange: OutputEmitterRef<string> = output<string>();
  selectedFromLeiProfile: OutputEmitterRef<ILeiProfile> = output<ILeiProfile>();

  public ngControl: NgControl | null = inject(NgControl, { optional: true, self: true });
  public options$!: Observable<ICompanyNamesAutocomplete[]>;
  public value: string | { name: string; lei: ILeiProfile } = '';
  public selectedLei = '';
  public throttledNameEmitFn = throttle((v) => this.nameChange.emit(v), 400);

  private readonly _formDisabled$ = new BehaviorSubject<boolean>(
    Boolean(this.ngControl?.control?.disabled)
  );
  private readonly formDisabled: Signal<boolean> = toSignal(this._formDisabled$, {
    requireSync: true,
  });

  public fieldDisabled: Signal<boolean> = computed(() => this.disabled() || this.formDisabled());

  public static getAllNamesFromLeiProfile(data: ILeiProfile[]): ICompanyNamesAutocomplete[] {
    const res: ICompanyNamesAutocomplete[] = [];

    data.forEach((lei) => {
      const item: ICompanyNamesAutocomplete = {
        lei,
        names: [lei.attributes.entity.legalName.name],
      };

      lei.attributes.entity.otherNames.forEach((otherName) => {
        item.names.push(otherName.name);
      });
      res.push(item);
    });

    return res;
  }

  @ViewChild(MatInput) matInput!: MatInput;
  @ViewChild(MatAutocompleteTrigger) trigger!: MatAutocompleteTrigger;

  onTouched: (args?: unknown) => void = () => {};
  onChange: (args?: unknown) => void = () => {};

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    if (this.ngControl) {
      this.matInput.ngControl = this.ngControl;

      if (this.ngControl.control) {
        const control = this.ngControl.control;

        this.options$ = control.valueChanges.pipe(
          filter((res) => Boolean(res) && !this.isLeiRequestBlocked()),
          debounceTime(400),
          switchMap((value: string) => {
            const _params = {
              name: value,
              country: (this.country() as ICountry)?.code,
            };

            return this._leiDatabaseService.getLEIByFreeText(_params);
          }),
          map((res: IPaginatedResponse<ILeiProfile[]>): ICompanyNamesAutocomplete[] => {
            let items: ILeiProfile[] = res.items;
            const leiFilter = this.searchOnlyByLEI();

            if (leiFilter.length) {
              items = items.filter((lei) => leiFilter.includes(lei.id));
            }

            return EntityNameInputComponent.getAllNamesFromLeiProfile(items);
          }),
          untilDestroyed(this)
        );
      }
    }
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnTouched(fn: (args: unknown) => void): void {
    this.onTouched = fn;
  }

  registerOnChange(fn: (args: unknown) => void): void {
    this.onChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._formDisabled$.next(isDisabled);
  }

  get isFieldEmpty(): boolean {
    return !this.value;
  }

  public handleValChange(value: string | { name: string; lei: ILeiProfile }): void {
    if (typeof value === 'string') {
      this.value = value;
      this.throttledNameEmitFn(value);
    } else {
      this.value = value.name;
    }

    this.onChange(this.value);
  }

  public handleSelect(event: MatAutocompleteSelectedEvent): void {
    const { name, lei }: { name: string; lei: ILeiProfile } = event.option.value;
    const sameName: boolean =
      (typeof this.value === 'string' ? this.value : this.value.name) === name;

    // Resetting the input to trigger change detection in case the same name is selected again
    if (sameName) {
      this.onChange(null);

      setTimeout(() => {
        this.value = name;
        this.onChange(name);
      });
    } else {
      this.value = name;
      this.onChange(this.value);
    }
    this.selectedFromLeiProfile.emit(lei);
  }

  public displayFn(item: ILeiProfile | string): string {
    if (typeof item === 'string') {
      return item;
    }

    return item && getCompanyNameFromLei(item);
  }

  public searchLEIDatabase(event: MouseEvent): void {
    event.preventDefault();

    if (!this.value || !(this.country() as ICountry)?.code) {
      this._notifyService.notifyCautionOnly('Please select entity and country.');
      return;
    }

    const _params = {
      name: typeof this.value === 'string' ? this.value : this.value.name,
      country: (this.country() as ICountry)?.code,
    };

    this._leiDatabaseService
      .getLEIByFreeText(_params)
      .pipe(
        switchMap((res: IPaginatedResponse<ILeiProfile[]>) => {
          if (!res.items) {
            this._notifyService.notifyCautionOnly(
              'Not found. Please refine your search criteria and try again.'
            );
            return EMPTY;
          }

          return this._matDialog
            .open(LeiSearchDialogComponent, {
              data: {
                list: res.items,
              },
              width: DIALOG_SIZE.MODAL_MD,
            })
            .afterClosed();
        }),
        filter(Boolean),
        untilDestroyed(this)
      )
      .subscribe((item: ILeiProfile) => {
        this.value = getCompanyNameFromLei(item);
        this.selectedFromLeiProfile.emit(item);
        this.trigger.closePanel();
      });
  }
}
